import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, MapPin, Check, Minus, Plus, Users, CalendarDays } from "lucide-react";
import { Calendar } from "./Calendar";
import { useLang } from "../i18n/LanguageContext";
import { TRIP_META } from "../lib/config";

const BookingPanel = ({ id, onPay, refreshKey }) => {
  const { t, money, shortDate } = useLang();
  const meta = TRIP_META[id];
  const [date, setDate] = useState(null);
  const [left, setLeft] = useState(null);
  const [people, setPeople] = useState(2);
  const max = Math.min(10, left ?? 10);
  const total = people * meta.price;

  const select = (d, spots) => {
    setDate(d); setLeft(spots);
    if (people > spots) setPeople(spots);
  };

  return (
    <div className="border-t border-slate-100 p-5 sm:p-6" data-testid={`booking-panel-${id}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ink)]"><CalendarDays className="h-4 w-4 text-[var(--coral)]" />{t.trips.pickDate}</span>
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-600"><i className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />{t.trips.live}</span>
      </div>
      <Calendar tripId={id} selected={date} onSelect={select} refreshKey={refreshKey} />

      <div className="mt-5 flex items-center justify-between rounded-2xl bg-slate-50 p-2 pl-4">
        <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ink)]"><Users className="h-4 w-4 text-[var(--coral)]" />{t.trips.people}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setPeople((p) => Math.max(1, p - 1))} disabled={people <= 1} className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:opacity-30" data-testid={`people-minus-${id}`} aria-label="Fewer people"><Minus className="h-4 w-4" /></button>
          <span className="font-display w-9 text-center text-lg font-bold tabular-nums" data-testid={`people-count-${id}`}>{people}</span>
          <button type="button" onClick={() => setPeople((p) => Math.min(max, p + 1))} disabled={people >= max} className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:opacity-30" data-testid={`people-plus-${id}`} aria-label="More people"><Plus className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="mt-5 flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t.trips.total}</p>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.p key={total} initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }} transition={{ duration: 0.25 }} className="font-display text-3xl font-extrabold tracking-tight text-[var(--ink)]" data-testid={`total-${id}`}>{money(total)}</motion.p>
          </AnimatePresence>
        </div>
        <div className="text-right text-xs text-slate-500" data-testid={`selection-summary-${id}`}>
          {date ? (<><span className="block font-semibold text-[var(--ink)]">{shortDate(date)} · {meta.time}</span>{left != null && <span>{left} {t.trips.spotsLeft}</span>}</>) : <span>{t.trips.selectDateFirst}</span>}
        </div>
      </div>
      <button type="button" disabled={!date} onClick={() => onPay({ id, date, people, total })} className="btn-coral mt-4 h-14 w-full text-base" data-testid={`pay-btn-${id}`}>
        {date ? `${t.trips.pay} ${money(total)}` : t.trips.payNow}
      </button>
    </div>
  );
};

export const TripCard = ({ id, index, onPay, refreshKey }) => {
  const { t, money } = useLang();
  const meta = TRIP_META[id];
  const copy = t.trips.items[id];
  const hours = meta.duration_min / 60;
  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay: index * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
      className="card-lift flex flex-col overflow-hidden rounded-[1.6rem] bg-white shadow-[0_20px_50px_-30px_rgba(11,17,32,.25)] ring-1 ring-black/5"
      data-testid={`trip-card-${id}`}
    >
      <div className="img-zoom relative aspect-[4/3] overflow-hidden">
        <img src={meta.image} alt={copy.title} className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <div className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-[var(--ink)] backdrop-blur"><Clock className="h-3.5 w-3.5 text-[var(--coral)]" />{hours} {t.trips.hours} · {meta.time}</div>
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 text-white">
          <h3 className="font-display text-2xl font-bold leading-tight tracking-tight" data-testid={`trip-title-${id}`}>{copy.title}</h3>
          <div className="text-right leading-none"><span className="font-display block text-3xl font-extrabold tracking-tight" data-testid={`trip-price-${id}`}>{money(meta.price)}</span><span className="text-[11px] font-medium text-white/80">{t.trips.perPerson}</span></div>
        </div>
      </div>
      <div className="p-5 sm:p-6">
        <p className="text-sm leading-relaxed text-[var(--mist)]">{copy.tagline}</p>
        <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-400">{t.trips.included}</p>
        <ul className="mt-2 grid grid-cols-1 gap-1.5 text-sm text-[var(--ink-2)]">
          {copy.includes.map((inc) => <li key={inc} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{inc}</li>)}
        </ul>
        <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5 text-[var(--coral)]" />{copy.meeting}</p>
      </div>
      <BookingPanel id={id} onPay={onPay} refreshKey={refreshKey} />
    </motion.article>
  );
};
